import type { Dayjs } from 'dayjs';
import type { TextFieldProps } from '@mui/material/TextField';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import type { MobileDateTimePickerProps } from '@mui/x-date-pickers/MobileDateTimePicker';

import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';

import { formatPatterns } from 'src/utils/format-time';
import {
  ADMIN_TIME_ZONE,
  getCalendarDate,
  getDateInTimeZone,
  getDayBoundaryInTimeZone,
} from 'src/utils/time-zone';

// ----------------------------------------------------------------------

type RHFDatePickerProps = DatePickerProps<Dayjs> & {
  name: string;
  valueAsString?: boolean;
  endOfDay?: boolean;
};

export function RHFDatePicker({
  name,
  valueAsString,
  endOfDay = true,
  timezone: timezoneProp,
  label,
  slotProps,
  ...other
}: RHFDatePickerProps) {
  const { control } = useFormContext();
  const timezone = timezoneProp ?? (valueAsString ? undefined : ADMIN_TIME_ZONE);
  const pickerLabel =
    timezone === ADMIN_TIME_ZONE && typeof label === 'string' && !label.includes('(ET)')
      ? `${label} (ET)`
      : label;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <DatePicker
          {...field}
          value={
            field.value !== null && field.value !== undefined
              ? timezone
                ? getDateInTimeZone(field.value, timezone)
                : getCalendarDate(field.value)
              : null
          }
          onChange={(newValue) => {
            if (!newValue?.isValid()) {
              field.onChange(null);
            } else if (valueAsString) {
              field.onChange(newValue.format('YYYY-MM-DD'));
            } else if (timezone) {
              field.onChange(getDayBoundaryInTimeZone(newValue, timezone, endOfDay));
            } else {
              if (endOfDay) {
                field.onChange(
                  dayjs(newValue)
                    .set('hour', 23)
                    .set('minute', 59)
                    .set('second', 59)
                    .toDate()
                    .getTime()
                );
              } else {
                field.onChange(dayjs(newValue).startOf('day').toDate().getTime());
              }
            }
          }}
          label={pickerLabel}
          timezone={timezone}
          format={formatPatterns.split.date}
          slotProps={{
            actionBar: { actions: ['today'] },
            ...slotProps,
            textField: {
              fullWidth: true,
              error: !!error,
              helperText: error?.message ?? (slotProps?.textField as TextFieldProps)?.helperText,
              ...slotProps?.textField,
            },
          }}
          {...other}
        />
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFMobileDateTimePickerProps = MobileDateTimePickerProps<Dayjs> & {
  name: string;
};

export function RHFMobileDateTimePicker({
  name,
  timezone = ADMIN_TIME_ZONE,
  label,
  slotProps,
  ...other
}: RHFMobileDateTimePickerProps) {
  const { control } = useFormContext();
  const pickerLabel =
    timezone === ADMIN_TIME_ZONE && typeof label === 'string' && !label.includes('(ET)')
      ? `${label} (ET)`
      : label;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <MobileDateTimePicker
          {...field}
          value={
            field.value !== null && field.value !== undefined
              ? getDateInTimeZone(field.value, timezone)
              : null
          }
          onChange={(newValue) => field.onChange(newValue?.isValid() ? newValue.valueOf() : null)}
          label={pickerLabel}
          timezone={timezone}
          format={formatPatterns.split.dateTime}
          slotProps={{
            ...slotProps,
            textField: {
              fullWidth: true,
              error: !!error,
              helperText: error?.message ?? (slotProps?.textField as TextFieldProps)?.helperText,
              ...slotProps?.textField,
            },
          }}
          {...other}
        />
      )}
    />
  );
}
